import React, { forwardRef } from 'react';
import { css, cx } from '@emotion/css';
import { dateToToLongMonthString, getDayAbbreviations, getDateFromDateOrNow, isValidEndDate, SelectedState, HoverState, } from '../DateModules/shared';
import { Month, MonthHeader, Week, CalendarBackButton, CalendarForwardButton, DayHeader, } from '../DateModules/Calendar';
import { useLocalizationContext, useThemeContext } from '@contrast-security-inc/design-system-foundations';
import { datePickerCSS } from '../DatePicker/DatePicker';
import { useDatePicker } from '../DateModules/useDatePicker';
import { getDateAtEndOfDay, getDateAtStartOfDay } from '../utils/helperFunctions';
export const validateRange = ({ startDate, endDate, minDate, maxDate }) => {
    if (minDate && maxDate && getDateFromDateOrNow(minDate) >= getDateFromDateOrNow(maxDate)) {
        console.warn(`minDate must be less than maxDate`);
    }
    if (startDate && endDate) {
        if (startDate > endDate) {
            console.warn(`startDate must be less than endDate`);
        }
    }
    if (minDate && startDate && getDateFromDateOrNow(minDate) > startDate) {
        console.warn(`startDate must be greater than or equal to minDate`);
    }
    if (maxDate && endDate && isValidEndDate(maxDate, endDate)) {
        console.warn(`endDate must be less than or equal to maxDate`);
    }
};
export const getHoverState = ({ date }, hoveredDate, startDate, endDate) => {
    if (hoveredDate) {
        if (date.getTime() === hoveredDate.getTime() && (!startDate || startDate.getTime() > hoveredDate.getTime())) {
            return HoverState.HOVERED;
        }
        if (hoveredDate.getTime() === date.getTime() &&
            endDate &&
            getDateAtStartOfDay(endDate).getTime() < hoveredDate.getTime()) {
            return HoverState.HOVERED_END_OF_HOVER_RANGE;
        }
        if (endDate && date.getTime() >= getDateAtStartOfDay(endDate).getTime() && date.getTime() < hoveredDate.getTime()) {
            return HoverState.HOVERED_WITHIN_HOVER_RANGE;
        }
    }
};
export const getSelectedState = ({ selected: isDateSelected, date }, startDate, endDate) => {
    const startDateAtStartOfDay = startDate && getDateAtStartOfDay(startDate);
    const endDateAtStartOfDay = endDate && getDateAtStartOfDay(endDate);
    if (isDateSelected &&
        startDateAtStartOfDay &&
        endDateAtStartOfDay &&
        startDateAtStartOfDay.getTime() === endDateAtStartOfDay.getTime()) {
        return SelectedState.SELECTED;
    }
    if (startDateAtStartOfDay && startDateAtStartOfDay.getTime() === date.getTime()) {
        return SelectedState.SELECTED_BEGINNING_OF_SELECTION;
    }
    if (endDateAtStartOfDay && endDateAtStartOfDay.getTime() === date.getTime()) {
        return SelectedState.SELECTED_END_OF_SELECTION;
    }
    if (startDateAtStartOfDay &&
        endDateAtStartOfDay &&
        date.getTime() > startDateAtStartOfDay.getTime() &&
        date.getTime() < endDateAtStartOfDay.getTime()) {
        return SelectedState.SELECTED_WITHIN_SELECTION_RANGE;
    }
};
export const onDateClicked = (selectable, date, startDate, endDate) => {
    if (selectable) {
        // clear if the end date or start date is clicked
        if (endDate?.toLocaleDateString() === date.toLocaleDateString() ||
            startDate?.toLocaleDateString() === date.toLocaleDateString()) {
            return;
        }
        else {
            let newStartDate = startDate;
            if (!startDate || date.getTime() < startDate.getTime()) {
                newStartDate = date;
            }
            if (newStartDate) {
                const dateAtEndOfDay = getDateAtEndOfDay(date);
                return { start: newStartDate, end: dateAtEndOfDay };
            }
        }
    }
};
const isSelected = (startDate, endDate) => {
    if (startDate && endDate) {
        return [startDate, endDate];
    }
    else if (startDate)
        return [startDate];
    return [];
};
export const DateRangePicker = forwardRef(({ testId, className, minDate, maxDate, initialMonth = 'NOW', monthsToDisplay = 2, startDate, endDate, onStartDateSelected, onEndDateSelected, ...rest }, ref) => {
    validateRange({ startDate, endDate, minDate, maxDate });
    const { selectedTheme } = useThemeContext();
    const { locale } = useLocalizationContext();
    const selected = isSelected(startDate, endDate);
    const handleDateSelected = ({ date, selectable }) => {
        const { start: newStartDate, end: newEndDate } = onDateClicked(selectable, date, startDate, endDate) || {};
        if (!newEndDate) {
            onStartDateSelected();
            onEndDateSelected();
        }
        else {
            onStartDateSelected(newStartDate);
            onEndDateSelected(newEndDate);
        }
    };
    const { calendars, getDateProps, getBackProps, getForwardProps, hoveredDate, onDateMouseOver, onDateMouseOut } = useDatePicker({
        date: endDate || getDateFromDateOrNow(initialMonth),
        minDate,
        maxDate,
        monthsToDisplay,
        selected,
        onDateSelected: handleDateSelected,
    });
    return (React.createElement("div", { ...rest, "data-testid": testId, ref: ref, className: cx(className, datePickerCSS(monthsToDisplay, selectedTheme), 'ds-date-range-picker') },
        React.createElement("div", { className: css `
                  display: flex;
                  position: relative;
                ` },
            React.createElement(CalendarBackButton, { getButtonProps: (props) => getBackProps({ ...props, calendars }) }),
            React.createElement(CalendarForwardButton, { getButtonProps: (props) => getForwardProps({ ...props, calendars }) }),
            calendars.map((calendar, _calendarIndex) => {
                return (React.createElement(Month, { key: `${calendar.month}-${calendar.year}` },
                    React.createElement(MonthHeader, { month: dateToToLongMonthString(calendar.firstDayOfMonth, locale), year: calendar.year.toString() }),
                    React.createElement(DayHeader, { dayAbbreviations: getDayAbbreviations(locale) }),
                    calendar.weeks.map((week, weekIndex) => {
                        const key = `${calendar.year}-${calendar.month}-${weekIndex}`;
                        return (React.createElement(Week, { key: key, week: week, getDateProps: getDateProps, getHoverState: (currentDate) => getHoverState(currentDate, hoveredDate, startDate, endDate), getSelectedState: (currentDate) => getSelectedState(currentDate, startDate, endDate), onDateMouseOver: onDateMouseOver, onDateMouseOut: onDateMouseOut }));
                    })));
            }))));
});
DateRangePicker.displayName = 'DateRangePicker';
